<template>
	<v-container>
		<v-tabs-items v-model="tab" touchless>
			<v-tab-item key="schedule">
				<v-row v-if="userData" class="text-center">
					<v-col></v-col>
					<v-col class="mt-2">
						<v-row>
							<v-col class="mt-2">
								<p class="font-weight-regular">Hello {{userData.firstName}}, pick a shift you'd like to claim. You currently have {{ownedShiftsCount}} shifts.</p>
								<p class="font-weight-regular">This page does not work well in Firefox. Please use Chrome or Safari instead.</p>
								<p class="font-weight-regular">More information about shifts and volunteering <a href="https://etes2021.eu/volunteering/" target="_blank">here</a>.</p>
							</v-col>
						</v-row>
						<v-row class="text-center">
							<v-col class="mb-0 mt-0" style="height:48px">
								<v-btn
									v-if="selectedShiftClaimable"
									@click="claimShift"
								>Claim {{selectedShiftTitle}}</v-btn>
<!--								<v-btn-->
<!--									v-if="selectedShiftReleasable"-->
<!--									@click="unclaimShift"-->
<!--								>Release {{selectedShiftTitle}}</v-btn>-->
								<v-progress-circular
									v-if="!(schedule && shifts && schedule.length && shifts.length)"
									:size="96"
									color="primary"
									indeterminate
								></v-progress-circular>
							</v-col>
						</v-row>
					</v-col>
					<v-col class="mt-2">
						<p class="font-weight-regular">
							Red shifts not taken = event is illegal ({{this.redShiftsTaken}} / {{this.totalRed}} taken)<br/>
							Orange shifts not taken = event can not run ({{this.orangeShiftsTaken}} / {{this.totalOrange}} taken)<br/>
							Yellow shifts not taken = severe delays and inconveniences ({{this.yellowShiftsTaken}} / {{this.totalYellow}} taken)<br/>
							Green shifts not taken = mild delays or inconveniences ({{this.greenShiftsTaken}} / {{this.totalGreen}} taken) <br/>
						</p>
					</v-col>
				</v-row>
				<v-row v-if="userData" class="text-center">
					<v-col class="mb-4">
						<table width="100%" style="display: block; max-height: calc(100vh - 240px); overflow: auto; border-collapse: collapse">
							<template v-for="(row, rowIndex) in schedule">
								<tr :class="'scheduleRow ' + (rowIndex == 1 ? 'scheduleColHeader' : '')" v-bind:key="rowIndex">
									<td
										v-for="(cell, colIndex) in row"
										v-bind:key="colIndex"
										:class="'scheduleCell' + (colIndex === 0 ? ' scheduleRowHeader' : '')"
										:style="{'background-color': '#' + cell.color}"
									>
										<div
											v-if="cell.color === 'ffffff' || cell.color === '808080'"
											:class="colIndex === 0 ? 'scheduleRowHeaderContent' : 'scheduleCellContent'"
										>{{shifts && shifts[rowIndex] && shifts[rowIndex][colIndex]}}
										</div>
										<div
											v-else
											:class="'scheduleCellContent' + (isSelectable(rowIndex, colIndex) ? ' scheduleCellSelectable' : '') + (isSelected(rowIndex, colIndex) ? ' scheduleCellSelected' : '') + (isActive(rowIndex, colIndex) ? ' scheduleCellActive' : '')"
											@click="selectShift(rowIndex, colIndex)"
										>{{shifts && shifts[rowIndex] && shifts[rowIndex][colIndex]}}
										</div>
									</td>
								</tr>
							</template>
						</table>
					</v-col>
				</v-row>
			</v-tab-item>
			<v-tab-item key="overview" v-if="userData">
				<p>{{userData.firstName}} {{userData.lastName}} ({{userData.username}})</p>
				<table>
					<thead>
					<td style="width: 6em;">Day</td>
					<td style="width: 6em;">Time from</td>
					<td style="width: 20em;">Task</td>
					</thead>
					<tr
						v-for="(shift, shiftIdx) in selectedShifts"
						v-bind:key="shiftIdx"
					>
						<td>{{shift.day}}</td>
						<td>{{shift.time}}</td>
						<td>{{shift.task}}</td>
					</tr>
				</table>
			</v-tab-item>
			<v-tab-item key="team-agenda" v-if="userData">
				<TeamAgenda :allShiftsList="allShiftsList" :teamMembersById="teamMembersById"></TeamAgenda>
			</v-tab-item>
			<v-tab-item key="team" v-if="userData">
				<div v-for="(member, index) in teamMembers" :key="index">
					<p class="mb-0">{{member.firstName}} {{member.lastName}} ({{member.id}}){{member.isAdjudicator?' (Adjudicator)':''}}</p>
					<table class="mb-4 ml-4">
						<thead>
						<td style="width: 6em;">Day</td>
						<td style="width: 6em;">Time from</td>
						<td style="width: 20em;">Task</td>
						</thead>
						<tr
							v-for="(shift, shiftIdx) in selectedShiftsFor(member.id)"
							:key="`${index}-${shiftIdx}`"
						>
							<td>{{shift.day}}</td>
							<td>{{shift.time}}</td>
							<td>{{shift.task}}</td>
						</tr>
					</table>
				</div>
			</v-tab-item>
			<v-tab-item key="all-teams">
				<AllTeamAgendas :allShiftsList="allShiftsList" :userData="userData"></AllTeamAgendas>
			</v-tab-item>
		</v-tabs-items>

		<v-snackbar v-model="showErrorMessage">{{errorMessage}}</v-snackbar>
		<v-snackbar v-model="showSuccessMessage">Succeeded!</v-snackbar>
	</v-container>
</template>

<script>
	import axios from 'axios';
	import AllTeamAgendas from './AllTeamAgendas';
	import TeamAgenda from './TeamAgenda';

	function getColLetters(column) {
		const a1Notation = [];
		const totalAlphabets = "Z".charCodeAt() - "A".charCodeAt() + 1;
		let block = column;
		while (block >= 0) {
			a1Notation.unshift(
				String.fromCharCode((block % totalAlphabets) + "A".charCodeAt())
			);
			block = Math.floor(block / totalAlphabets) - 1;
		}
		return a1Notation.join("");
	}

	export default {
		name: 'ShiftSelector',

		components: {
			AllTeamAgendas,
			TeamAgenda,
		},

		data: () => ({
			shift: '',
			shiftsSheetId: '',
			errorMessage: '',
			showErrorMessage: false,
			showSuccessMessage: false,
			ownedShiftsCount: 0,
			shifts: [],
			allShiftsList: [],
			schedule: [],
			selectedShift: '',
			totalRed: 0,
			totalOrange: 0,
			totalYellow: 0,
			totalGreen: 0,
			redShiftsTaken: 0,
			orangeShiftsTaken: 0,
			yellowShiftsTaken: 0,
			greenShiftsTaken: 0,
			selectedShifts: [],
			teamMembers: [],
			teamMembersById: {},
		}),
		props: ['tab', 'token', 'userData'],
		watch: {
			async userData() {
				if (this.userData.isCaptain) {
					this.teamMembers = (await axios.get(`/teams/${this.userData.teamId}`)).data.members;
					this.teamMembersById = Object.fromEntries(this.teamMembers.map(m => [m.id, m]));
				}
			},
		},
		computed: {
			selectedShiftData: function() {
				return this.selectedShift && this.shifts && this.shifts.length && this.shifts[this.selectedShift.rowIndex][this.selectedShift.colIndex] || false;
			},
			selectedShiftClaimable: function() {
				return this.selectedShift && this.selectedShiftData === false;
			},
			selectedShiftReleasable: function() {
				return this.selectedShift && this.selectedShiftData === this.userData.username;
			},
			selectedShiftName: function() {
				if (!this.selectedShift) {
					return;
				}
				return this.getCellCode(this.selectedShift.colIndex, this.selectedShift.rowIndex);
			},
			selectedShiftTitle: function() {
				return `${this.shifts[this.selectedShift.rowIndex][0]} ${this.shifts[0][this.selectedShift.colIndex]} ${this.shifts[1][this.selectedShift.colIndex]} - ${this.shifts[2][this.selectedShift.colIndex]}`;
			},
			uncancelledTeamMembers: function() {
				return this.teamMembers.filter(m => !m.isCancelled);
			}
		},
		created: function() {
			axios.get(`/shiftsSheetInfo`).then(async ({data}) => {
				this.shiftsSheetId = data.id;
				this.shiftsSheetOffsetTop = data.offsetTop;
				this.shiftsSheetOffsetLeft = data.offsetLeft;

				const gapi = await window.gapiLoaded;
				const sheetData = await gapi.client.sheets.spreadsheets.get({
					spreadsheetId: this.shiftsSheetId,
					ranges:`${this.getCellCode(0, 0)}:zz999`,
					includeGridData: true,
				});
				function sheetToHtmlColor(sheetColor) {
					function toHex(n) {
						return ('0' + (Math.floor((n || 0) * 255)).toString(16)).substr(-2);
					}
					return `${toHex(sheetColor.red)}${toHex(sheetColor.green)}${toHex(sheetColor.blue)}`;
				}
				this.schedule = sheetData.result.sheets[0].data[0].rowData.map(row => row.values.map(value => ({color: sheetToHtmlColor(value.effectiveFormat.backgroundColor)})))
				const allShifts = this.schedule.flat().flat().filter(e => e.color != '808080' && e.color != 'FFFFFF');
				this.totalRed = allShifts.filter(s => s.color === 'ff0000').length;
				this.totalOrange = allShifts.filter(s => s.color === 'ff8000').length;
				this.totalYellow = allShifts.filter(s => s.color === 'ffff00').length;
				this.totalGreen = allShifts.filter(s => s.color === '00ff00').length;
				await this.updateSheet();
			});
		},
		methods: {
			getCellCode(col, row) {
				return `${getColLetters(col + this.shiftsSheetOffsetLeft)}${row + this.shiftsSheetOffsetTop}`;
			},
			isSelectable(rowIndex, colIndex) {
				return !(this.shifts && this.shifts[rowIndex] && this.shifts[rowIndex][colIndex]);
			},
			isSelected(rowIndex, colIndex) {
				return this.shifts && this.shifts[rowIndex] && this.shifts[rowIndex][colIndex] === this.userData.username;
			},
			isActive(rowIndex, colIndex) {
				return this.selectedShift.rowIndex === rowIndex && this.selectedShift.colIndex === colIndex;
			},
			selectShift(rowIndex, colIndex) {
				if (this.isSelectable(rowIndex, colIndex) || this.isSelected(rowIndex, colIndex)) {
					this.selectedShift = {rowIndex, colIndex};
				}
			},
			claimShift: async function() {
				try {
					const result = await axios.post(`/shifts/${this.selectedShiftName}/claims`);
					console.log('claim result', result);
					this.updateSheet();
					this.showSuccessMessage = true;
				} catch (e) {
					console.error('request failed', e.response.data);
					this.errorMessage = e.response.data;
					this.showErrorMessage = true;
				}
			},
			unclaimShift: async function() {
				try {
					const result = await axios.delete(`/shifts/${this.selectedShiftName}/claims/`);
					console.log('unclaim result', result);
					this.updateSheet();
					this.showSuccessMessage = true;
				} catch (e) {
					console.error('request failed', e.response.data);
					this.errorMessage = e.response.data;
					this.showErrorMessage = true;
				}
			},
			selectedShiftsFor(id) {
				return this.allShiftsList.filter(s => s.owner === id);
			},
			updateSheet: async function() {
				const gapi = await window.gapiLoaded;
				const sheetData = await gapi.client.sheets.spreadsheets.values.get({spreadsheetId:this.shiftsSheetId, range:`${this.getCellCode(0, 0)}:zz999`});
				this.shifts = sheetData.result.values;
				this.ownedShiftsCount = sheetData.result.values.flat().filter(id => id === this.userData.username).length;
				let redShiftsTaken = 0;
				let orangeShiftsTaken = 0;
				let yellowShiftsTaken = 0;
				let greenShiftsTaken = 0;
				const selectedShifts = [];
				const allShifts = [];
				for (let s = 0 ; s < this.schedule.length ; ++s) {
					for (let t = 0 ; t < this.schedule[0].length ; ++t) {
						if (this.schedule[s][t].color === 'ff0000' && this.shifts[s][t]) {
							++redShiftsTaken;
						}
						if (this.schedule[s][t].color === 'ff8000' && this.shifts[s][t]) {
							++orangeShiftsTaken;
						}
						if (this.schedule[s][t].color === 'ffff00' && this.shifts[s][t]) {
							++yellowShiftsTaken;
						}
						if (this.schedule[s][t].color === '00ff00' && this.shifts[s][t]) {
							++greenShiftsTaken;
						}
						if (this.shifts[s][t] === this.userData.username) {
							selectedShifts.push({day: this.shifts[0][t], time: `${this.shifts[1][t]}`, task: this.shifts[s][0]});
						}
						allShifts.push({
							shiftIdx: s,
							timeslotIdx: t,
							owner: this.shifts[s][t],
							day: this.shifts[0][t],
							time: `${this.shifts[1][t]}`,
							task: this.shifts[s][0],
						});
					}
				}
				this.redShiftsTaken = redShiftsTaken;
				this.orangeShiftsTaken = orangeShiftsTaken;
				this.yellowShiftsTaken = yellowShiftsTaken;
				this.greenShiftsTaken = greenShiftsTaken;
				this.selectedShifts = selectedShifts;
				this.allShiftsList = allShifts;
			},
		}

	}
</script>

<style scoped>
	.scheduleRow {
		border-spacing: 0;
	}
	.scheduleCell {
		border-style: solid;
		border-width: 1px;
		border-color: lightgray;
		border-spacing: 0;
		/*is ignored but makes inner divs size normally*/
		height: 1px;
	}
	.scheduleCellContent {
		width: 100px;
		height: 100%;
	}

	.scheduleCellSelectable {
		cursor: pointer;
	}

	.scheduleCellSelectable:hover {
		background-color: #aaa6;
		transition: all 0.1s ease-in-out;
	}

	.scheduleCellSelected {
		cursor: pointer;
		background-color: #DDDB;
		width: 100%;
		height: 100%;
		transition: all 0.1s ease-in-out;
	}

	.scheduleCellSelected:hover {
		background-color: #aaa6;
		transition: all 0.1s ease-in-out;
	}

	.scheduleCellActive {
		background-color: #ccc8;
		width: 100%;
		height: 100%;
		transition: all 0.1s ease-in-out;
	}

	.scheduleRowHeaderContent {
		width: 200px;
	}

	.scheduleRowHeader {
		position: sticky;
		left: 0;
	}

	.scheduleColHeader {
		position: sticky;
		top: 0;
	}

	.scheduleButton {
		width: 100%;
	}
</style>
